import { Capacitor } from '@capacitor/core'
import { i18n } from '@lingui/core'
import { t } from '@lingui/macro'
import { QRCodeSVG } from 'qrcode.react'
import { useNavigate } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { H4 } from '@/@bothrs-ui/web/components/typography/Heading'
import { globalDefinitions } from '@/defined-translations'
import { RelatedTriggers } from '@/modules/shared/components/related-items/RelatedTriggers'
import { formatLocale } from '@/modules/shared/helpers/locale'
import { useTaskStatusMetaData } from '../../hooks/useTaskStatusMetaData'
import { StTaskDetailParagraph, TaskDetailLayout } from '../detail-layout/TaskDetailLayout'

import type { RemecareTask } from '../../types/task.types'

type Props = {
  task: RemecareTask
}

export const RemecareDetail = ({ task }: Props) => {
  // Hooks
  // ------------------------------------------------------------------------- /
  const theme = useTheme()
  const navigate = useNavigate()

  const { daysTillExpiry } = useTaskStatusMetaData(task)

  // Template variables
  // ------------------------------------------------------------------------- /

  const isCompleted = task.status === 'completed'
  const isExpired = task.status === 'expired'

  const showQR = !Capacitor.isNativePlatform() && task.remecareUrl && !isCompleted && !isExpired

  // Methods
  // ------------------------------------------------------------------------- /
  const handleCtaPress = () => {
    if (isCompleted || isExpired) {
      navigate('..', { relative: 'path', state: { taskId: task.id } })
    }
  }

  const getStatusDisplay = (): string => {
    if (isCompleted) {
      return i18n._(globalDefinitions.task.Remecare.completed, {
        date: formatLocale(task.updatedAt, 'dd/MM/yyyy'),
      })
    }

    if (isExpired) {
      return i18n._(globalDefinitions.task.expired)
    }

    if (daysTillExpiry === 0) {
      return i18n._(globalDefinitions.task.Remecare.expires_today)
    }

    if (daysTillExpiry === 1) {
      return i18n._(globalDefinitions.task.Remecare.expires_in_one_day)
    }

    if (daysTillExpiry && daysTillExpiry > 1) {
      return i18n._(globalDefinitions.task.Remecare.expires_in_x_days, {
        count: daysTillExpiry,
      })
    }

    return i18n._(globalDefinitions.task.no_expiry, {
      date: formatLocale(task.createdAt, 'dd/MM/yyyy'),
    })
  }

  // Render
  // ------------------------------------------------------------------------- /

  const renderContent = () => {
    if (showQR) {
      return (
        <FlexColumn gap={theme.UI.Spacing.Space3}>
          <FlexColumn gap={theme.UI.Spacing.Space2}>
            <H4 noMargin>
              {t({
                id: 'remecare-detail.desktop.title',
                message: 'Ga naar de Remecare app om deze actie te voltooien',
              })}
            </H4>
            <StTaskDetailParagraph>
              {t({
                id: 'remecare-detail.desktop.body',
                message:
                  'Sommige taken voer je direct uit in MijnZiekenhuis, zoals afspraken beheren, omdat ze direct verband houden met je ziekenhuiservaring. Andere taken die gericht zijn op je gepersonaliseerde zorgtraject voltooi je in Remecare.',
              })}
            </StTaskDetailParagraph>
          </FlexColumn>

          <QrContainer>
            <QRCodeSVG value={task.remecareUrl} bgColor={theme.Colors.Neutrals.Lighter} />
            <StTaskDetailParagraph style={{ textAlign: 'center' }}>
              {t({
                id: 'remecare-detail.desktop.qr',
                message: 'Scan de QR code om de Remecare app te openen',
              })}
            </StTaskDetailParagraph>
          </QrContainer>
        </FlexColumn>
      )
    }

    if (isExpired) {
      return (
        <FlexColumn>
          <StTaskDetailParagraph>
            {t({
              id: 'remecare-detail.expired.title',
              message: 'Deze taak werd helaas niet tijdig afgewerkt en is intussen afgesloten.',
            })}
          </StTaskDetailParagraph>
        </FlexColumn>
      )
    }

    return null
  }

  return (
    <TaskDetailLayout>
      <TaskDetailLayout.Header task={task} statusLabel={getStatusDisplay()} />
      <TaskDetailLayout.Content>
        <FlexColumn gap={theme.UI.Spacing.Space4}>
          {renderContent()}

          {/* Related activities */}
          {task?.trigger && <RelatedTriggers trigger={task.trigger} />}
        </FlexColumn>
      </TaskDetailLayout.Content>
      {showQR ? null : (
        <TaskDetailLayout.Footer>
          <Button variant='Primary' onClick={handleCtaPress}>
            {isCompleted || isExpired
              ? i18n._(globalDefinitions.close)
              : t({
                  id: 'remecare-detail.cta.start',
                  message: 'Voltooi in Remecare App ',
                })}
          </Button>
        </TaskDetailLayout.Footer>
      )}
    </TaskDetailLayout>
  )
}

const QrContainer = styled.div`
  ${({ theme }) => `
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.UI.SpacingPx.Space4};
    background-color: ${theme.Colors.Neutrals.Lighter};
    padding: ${theme.UI.SpacingPx.Space8};
    border-radius: ${theme.UI.SpacingPx.Space2};
  `}
`
